import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import {
  Smartphone,
  QrCode,
  Users,
  Plus,
  Copy,
  RefreshCw,
  Trash2,
  LogOut,
  Link2,
  Shield,
  ShieldOff,
  Crown,
  UserMinus,
  UserPlus,
  Search,
  Megaphone,
  Send,
  Power,
  PowerOff,
  X,
  Check,
  Hash,
  Image,
  Eye,
  ChevronLeft,
  ChevronRight,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  Bold,
  Italic,
  Strikethrough,
  Code,
  CalendarClock,
  GitBranch,
  ImageIcon,
  FolderOpen,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Separator } from '@/components/ui/separator'
import { Checkbox } from '@/components/ui/checkbox'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { toast } from 'sonner'
import { useLanguage } from '@/i18n/LanguageContext'
import { useQueryClient } from '@tanstack/react-query'
import { BASE, getAuthHeaders } from '@/api/client'
import {
  useWhatsappStatus,
  useCreateInstance,
  useConnectInstance,
  useDisconnectInstance,
  useRestartInstance,
  useDeleteInstance,
  useWhatsappGroups,
  useCreateGroup,
  useGroupInviteCode,
  useRevokeInviteCode,
  useAddParticipants,
  useRemoveParticipants,
  usePromoteParticipants,
  useDemoteParticipants,
  useUpdateGroup,
  useUpdateGroupSetting,
  useLeaveGroup,
  useToggleEphemeral,
  useSendGroupInvite,
  useWhatsappChannels,
  useSendToChannel,
  useWhatsappBroadcastHistory,
  useDeleteWhatsappBroadcast,
  useCancelWhatsappBroadcast,
} from '@/api/hooks/useWhatsapp'
import { useMediaFolders } from '@/api/hooks/useMediaLibrary'
import { FolderTreePicker } from '@/components/FolderTreePicker'
import type { WhatsappGroup, WhatsappBroadcastFlowStep } from '@/types'

// ─── Session Tab ─────────────────────────────────────────

function SessionTab() {
  const { t } = useLanguage()
  const { data: instance, isLoading, refetch } = useWhatsappStatus()
  const createInstance = useCreateInstance()
  const connectInstance = useConnectInstance()
  const disconnectInstance = useDisconnectInstance()
  const restartInstance = useRestartInstance()
  const deleteInstance = useDeleteInstance()

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  // Auto-poll when connecting
  useEffect(() => {
    if (instance?.status !== 'connecting' && instance?.status !== 'created') return
    const interval = setInterval(() => refetch(), 5000)
    return () => clearInterval(interval)
  }, [instance?.status, refetch])

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  // No instance yet
  if (!instance?.exists) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-4 py-12">
          <Smartphone className="h-16 w-16 text-muted-foreground" />
          <h3 className="text-lg font-semibold">{t('Nenhuma sessao WhatsApp')}</h3>
          <p className="text-sm text-muted-foreground text-center max-w-md">
            {t('Crie uma sessao para conectar seu WhatsApp e gerenciar grupos e canais.')}
          </p>
          <Button
            onClick={() => createInstance.mutate(undefined, {
              onSuccess: () => toast.success(t('Sessao criada! Escaneie o QR Code.')),
              onError: (e) => toast.error(e.message),
            })}
            disabled={createInstance.isPending}
          >
            <Plus className="mr-2 h-4 w-4" />
            {createInstance.isPending ? t('Criando...') : t('Criar Sessao WhatsApp')}
          </Button>
        </CardContent>
      </Card>
    )
  }

  const isConnected = instance.status === 'open'
  const isConnecting = instance.status === 'connecting' || instance.status === 'created'

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <Smartphone className="h-5 w-5" />
            {t('Sessao WhatsApp')}
            <Badge variant={isConnected ? 'default' : 'secondary'} className={isConnected ? 'bg-green-600' : ''}>
              {isConnected ? t('Conectado') : isConnecting ? t('Aguardando conexao') : t('Desconectado')}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {instance.phoneNumber && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">{t('Telefone')}:</span>
              <span className="font-mono font-medium">{instance.phoneNumber}</span>
            </div>
          )}

          {instance.connectedAt && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">{t('Conectado desde')}:</span>
              <span>{new Date(instance.connectedAt).toLocaleString('pt-BR')}</span>
            </div>
          )}

          {/* QR Code */}
          {!isConnected && instance.qrCode && (
            <div className="flex flex-col items-center gap-3 py-4">
              <QrCode className="h-6 w-6 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">{t('Escaneie o QR Code com seu WhatsApp')}</p>
              <div className="rounded-lg border bg-white p-3">
                <img
                  src={instance.qrCode.startsWith('data:') ? instance.qrCode : `data:image/png;base64,${instance.qrCode}`}
                  alt="QR Code"
                  className="h-64 w-64"
                />
              </div>
              {isConnecting && (
                <p className="text-xs text-muted-foreground animate-pulse">{t('Aguardando leitura do QR Code...')}</p>
              )}
            </div>
          )}

          {/* Connect button when no QR yet */}
          {!isConnected && !instance.qrCode && (
            <Button
              onClick={() => connectInstance.mutate(undefined, {
                onSuccess: () => refetch(),
                onError: (e) => toast.error(e.message),
              })}
              disabled={connectInstance.isPending}
            >
              <QrCode className="mr-2 h-4 w-4" />
              {connectInstance.isPending ? t('Gerando QR Code...') : t('Gerar QR Code')}
            </Button>
          )}

          <Separator />

          {/* Action buttons */}
          <div className="flex flex-wrap gap-2">
            {isConnected && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => disconnectInstance.mutate(undefined, {
                  onSuccess: () => toast.success(t('Desconectado')),
                  onError: (e) => toast.error(e.message),
                })}
                disabled={disconnectInstance.isPending}
              >
                <PowerOff className="mr-2 h-4 w-4" />
                {t('Desconectar')}
              </Button>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={() => restartInstance.mutate(undefined, {
                onSuccess: () => { toast.success(t('Reiniciando...')); refetch() },
                onError: (e) => toast.error(e.message),
              })}
              disabled={restartInstance.isPending}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('Reiniciar')}
            </Button>

            <Button
              variant="destructive"
              size="sm"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {t('Excluir Sessao')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Delete confirm dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('Excluir sessao WhatsApp?')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t('Esta acao vai desconectar e remover completamente sua sessao WhatsApp. Voce precisara criar uma nova sessao para reconectar.')}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>{t('Cancelar')}</Button>
            <Button
              variant="destructive"
              onClick={() => deleteInstance.mutate(undefined, {
                onSuccess: () => { toast.success(t('Sessao removida')); setShowDeleteConfirm(false) },
                onError: (e) => toast.error(e.message),
              })}
              disabled={deleteInstance.isPending}
            >
              {deleteInstance.isPending ? t('Excluindo...') : t('Excluir')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ─── Groups Tab ──────────────────────────────────────────

function GroupsTab() {
  const { t } = useLanguage()
  const { data: instance } = useWhatsappStatus()
  const isConnected = instance?.status === 'open'

  const { data: groups, isLoading } = useWhatsappGroups(isConnected)
  const createGroup = useCreateGroup()
  const getInviteCode = useGroupInviteCode()
  const revokeInvite = useRevokeInviteCode()
  const addParticipants = useAddParticipants()
  const removeParticipants = useRemoveParticipants()
  const promoteParticipants = usePromoteParticipants()
  const demoteParticipants = useDemoteParticipants()
  const updateGroup = useUpdateGroup()
  const updateGroupSetting = useUpdateGroupSetting()
  const leaveGroup = useLeaveGroup()
  const toggleEphemeral = useToggleEphemeral()
  const sendGroupInvite = useSendGroupInvite()

  const [search, setSearch] = useState('')
  const [selectedGroup, setSelectedGroup] = useState<WhatsappGroup | null>(null)
  const [confirmLeaveId, setConfirmLeaveId] = useState<string | null>(null)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [showGroupDetail, setShowGroupDetail] = useState(false)

  // Create group form
  const [newGroupName, setNewGroupName] = useState('')
  const [newGroupDesc, setNewGroupDesc] = useState('')
  const [newGroupParticipants, setNewGroupParticipants] = useState('')

  // Add participant
  const [addParticipantInput, setAddParticipantInput] = useState('')

  // Invite code
  const [inviteCode, setInviteCode] = useState<string | null>(null)

  // Send invite to numbers
  const [inviteNumbers, setInviteNumbers] = useState('')

  // Edit group
  const [editSubject, setEditSubject] = useState('')
  const [editDescription, setEditDescription] = useState('')

  // Build instance JID from phone number for admin check
  const instanceJid = instance?.phoneNumber
    ? `${instance.phoneNumber.replace(/\D/g, '')}@s.whatsapp.net`
    : null

  const filteredGroups = useMemo(() => {
    if (!groups) return []
    // Only show groups where the instance is admin or superadmin
    const adminGroups = groups.filter(g => {
      if (!g.participants || !instanceJid) return false
      return g.participants.some(
        p => (p.phoneNumber === instanceJid || p.id === instanceJid) && (p.admin === 'admin' || p.admin === 'superadmin')
      )
    })
    if (!search) return adminGroups
    const q = search.toLowerCase()
    return adminGroups.filter(g => g.subject?.toLowerCase().includes(q) || g.desc?.toLowerCase().includes(q))
  }, [groups, search, instanceJid])

  if (!isConnected) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-12">
          <Power className="h-12 w-12 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{t('Conecte sua sessao WhatsApp para gerenciar grupos.')}</p>
        </CardContent>
      </Card>
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => <Skeleton key={i} className="h-20 w-full" />)}
      </div>
    )
  }

  const handleCreateGroup = () => {
    const participants = newGroupParticipants.split(/[,\n]/).map(p => p.trim()).filter(Boolean)
    if (!newGroupName || participants.length === 0) {
      toast.error(t('Preencha o nome e pelo menos um participante'))
      return
    }
    createGroup.mutate(
      { subject: newGroupName, description: newGroupDesc || undefined, participants },
      {
        onSuccess: () => {
          toast.success(t('Grupo criado!'))
          setShowCreateDialog(false)
          setNewGroupName('')
          setNewGroupDesc('')
          setNewGroupParticipants('')
        },
        onError: (e) => toast.error(e.message),
      }
    )
  }

  const openGroupDetail = (group: WhatsappGroup) => {
    setSelectedGroup(group)
    setEditSubject(group.subject || '')
    setEditDescription(group.desc || '')
    setInviteCode(null)
    setAddParticipantInput('')
    setShowGroupDetail(true)
  }

  const handleGetInvite = () => {
    if (!selectedGroup) return
    getInviteCode.mutate(selectedGroup.id, {
      onSuccess: (data) => setInviteCode(data.inviteUrl || data.inviteCode || null),
      onError: (e) => toast.error(e.message),
    })
  }

  const handleRevokeInvite = () => {
    if (!selectedGroup) return
    revokeInvite.mutate(selectedGroup.id, {
      onSuccess: () => { setInviteCode(null); toast.success(t('Link revogado')) },
      onError: (e) => toast.error(e.message),
    })
  }

  const handleAddParticipant = () => {
    if (!selectedGroup || !addParticipantInput.trim()) return
    const nums = addParticipantInput.split(/[,\n]/).map(p => p.trim()).filter(Boolean)
    addParticipants.mutate(
      { groupJid: selectedGroup.id, participants: nums },
      {
        onSuccess: () => { toast.success(t('Participante(s) adicionado(s)')); setAddParticipantInput('') },
        onError: (e) => toast.error(e.message),
      }
    )
  }

  const handleRemoveParticipant = (participantId: string) => {
    if (!selectedGroup) return
    removeParticipants.mutate(
      { groupJid: selectedGroup.id, participants: [participantId] },
      {
        onSuccess: () => toast.success(t('Participante removido')),
        onError: (e) => toast.error(e.message),
      }
    )
  }

  const handlePromote = (participantId: string) => {
    if (!selectedGroup) return
    promoteParticipants.mutate(
      { groupJid: selectedGroup.id, participants: [participantId] },
      {
        onSuccess: () => toast.success(t('Promovido a admin')),
        onError: (e) => toast.error(e.message),
      }
    )
  }

  const handleDemote = (participantId: string) => {
    if (!selectedGroup) return
    demoteParticipants.mutate(
      { groupJid: selectedGroup.id, participants: [participantId] },
      {
        onSuccess: () => toast.success(t('Rebaixado de admin')),
        onError: (e) => toast.error(e.message),
      }
    )
  }

  const handleUpdateGroup = () => {
    if (!selectedGroup) return
    const changes: Record<string, string> = {}
    if (editSubject !== selectedGroup.subject) changes.subject = editSubject
    if (editDescription !== (selectedGroup.desc || '')) changes.description = editDescription
    if (Object.keys(changes).length === 0) return
    updateGroup.mutate(
      { groupJid: selectedGroup.id, ...changes },
      {
        onSuccess: () => toast.success(t('Grupo atualizado')),
        onError: (e) => toast.error(e.message),
      }
    )
  }

  const handleToggleSetting = (action: string) => {
    if (!selectedGroup) return
    updateGroupSetting.mutate(
      { groupJid: selectedGroup.id, action },
      {
        onSuccess: () => toast.success(t('Configuracao atualizada')),
        onError: (e) => toast.error(e.message),
      }
    )
  }

  const handleLeaveGroup = () => {
    if (!selectedGroup) return
    leaveGroup.mutate(selectedGroup.id, {
      onSuccess: () => { toast.success(t('Voce saiu do grupo')); setShowGroupDetail(false) },
      onError: (e) => toast.error(e.message),
    })
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('Buscar grupos...')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {t('Criar Grupo')}
        </Button>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Users className="h-4 w-4" />
        <span>{filteredGroups.length} {t('grupo(s)')}</span>
      </div>

      {/* Group list */}
      <div className="grid gap-3">
        {filteredGroups.map((group) => (
          <Card
            key={group.id}
            className="cursor-pointer transition-colors hover:bg-accent/50"
            onClick={() => openGroupDetail(group)}
          >
            <CardContent className="flex items-center gap-4 py-3 px-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary font-bold text-sm">
                {group.pictureUrl ? (
                  <img src={group.pictureUrl} alt="" className="h-10 w-10 rounded-full object-cover" />
                ) : (
                  group.subject?.charAt(0)?.toUpperCase() || 'G'
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-sm">{group.subject}</div>
                {group.desc && (
                  <div className="truncate text-xs text-muted-foreground">{group.desc}</div>
                )}
              </div>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Users className="h-3.5 w-3.5" />
                <span>{group.size || group.participants?.length || 0}</span>
              </div>
              {group.announce && (
                <Badge variant="outline" className="text-xs">
                  <Megaphone className="mr-1 h-3 w-3" />
                  {t('Anuncio')}
                </Badge>
              )}
              {confirmLeaveId === group.id ? (
                <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                  <span className="text-xs text-destructive font-medium">{t('Sair?')}</span>
                  <Button
                    variant="destructive"
                    size="icon"
                    className="h-7 w-7"
                    disabled={leaveGroup.isPending}
                    onClick={(e) => {
                      e.stopPropagation()
                      leaveGroup.mutate(group.id, {
                        onSuccess: () => {
                          toast.success(t('Saiu do grupo'))
                          setConfirmLeaveId(null)
                        },
                        onError: () => toast.error(t('Erro ao sair do grupo')),
                      })
                    }}
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7"
                    onClick={(e) => {
                      e.stopPropagation()
                      setConfirmLeaveId(null)
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                  onClick={(e) => {
                    e.stopPropagation()
                    setConfirmLeaveId(group.id)
                  }}
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              )}
            </CardContent>
          </Card>
        ))}

        {filteredGroups.length === 0 && !isLoading && (
          <Card>
            <CardContent className="flex flex-col items-center gap-2 py-8">
              <Users className="h-10 w-10 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">{t('Nenhum grupo encontrado')}</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create Group Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('Criar Grupo')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">{t('Nome do grupo')}</label>
              <Input value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} placeholder={t('Meu Grupo')} />
            </div>
            <div>
              <label className="text-sm font-medium">{t('Descricao')} ({t('opcional')})</label>
              <Textarea value={newGroupDesc} onChange={(e) => setNewGroupDesc(e.target.value)} placeholder={t('Descricao do grupo...')} rows={2} />
            </div>
            <div>
              <label className="text-sm font-medium">{t('Participantes')} ({t('numeros separados por virgula')})</label>
              <Textarea
                value={newGroupParticipants}
                onChange={(e) => setNewGroupParticipants(e.target.value)}
                placeholder="5511999999999, 5522888888888"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>{t('Cancelar')}</Button>
            <Button onClick={handleCreateGroup} disabled={createGroup.isPending}>
              {createGroup.isPending ? t('Criando...') : t('Criar')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Group Detail Dialog */}
      <Dialog open={showGroupDetail} onOpenChange={setShowGroupDetail}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {selectedGroup?.subject}
            </DialogTitle>
          </DialogHeader>

          {selectedGroup && (
            <Tabs defaultValue="info" className="w-full">
              <TabsList className="w-full">
                <TabsTrigger value="info" className="flex-1">{t('Info')}</TabsTrigger>
                <TabsTrigger value="participants" className="flex-1">{t('Participantes')}</TabsTrigger>
                <TabsTrigger value="settings" className="flex-1">{t('Config')}</TabsTrigger>
              </TabsList>

              {/* Info tab */}
              <TabsContent value="info" className="space-y-4">
                <div>
                  <label className="text-sm font-medium">{t('Nome')}</label>
                  <Input value={editSubject} onChange={(e) => setEditSubject(e.target.value)} />
                </div>
                <div>
                  <label className="text-sm font-medium">{t('Descricao')}</label>
                  <Textarea value={editDescription} onChange={(e) => setEditDescription(e.target.value)} rows={3} />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleUpdateGroup} disabled={updateGroup.isPending}>
                    <Check className="mr-2 h-4 w-4" />
                    {t('Salvar')}
                  </Button>
                </div>

                <Separator />

                {/* Invite link */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t('Link de Convite')}</label>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={handleGetInvite} disabled={getInviteCode.isPending}>
                      <Link2 className="mr-2 h-4 w-4" />
                      {t('Obter Link')}
                    </Button>
                    {inviteCode && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { navigator.clipboard.writeText(inviteCode); toast.success(t('Link copiado!')) }}
                        >
                          <Copy className="mr-2 h-4 w-4" />
                          {t('Copiar')}
                        </Button>
                        <Button size="sm" variant="outline" onClick={handleRevokeInvite} disabled={revokeInvite.isPending}>
                          <X className="mr-2 h-4 w-4" />
                          {t('Revogar')}
                        </Button>
                      </>
                    )}
                  </div>
                  {inviteCode && (
                    <p className="text-xs font-mono text-muted-foreground break-all">{inviteCode}</p>
                  )}
                </div>

                <Separator />

                {/* Leave / Danger */}
                <Button variant="destructive" size="sm" onClick={handleLeaveGroup} disabled={leaveGroup.isPending}>
                  <LogOut className="mr-2 h-4 w-4" />
                  {t('Sair do Grupo')}
                </Button>
              </TabsContent>

              {/* Participants tab */}
              <TabsContent value="participants" className="space-y-4">
                {/* Add participant */}
                <div className="flex gap-2">
                  <Input
                    value={addParticipantInput}
                    onChange={(e) => setAddParticipantInput(e.target.value)}
                    placeholder={t('Numeros (virgula para varios)')}
                    className="flex-1"
                  />
                  <Button size="sm" onClick={handleAddParticipant} disabled={addParticipants.isPending}>
                    <UserPlus className="mr-2 h-4 w-4" />
                    {t('Adicionar')}
                  </Button>
                </div>

                {/* Participant list */}
                <div className="space-y-1 max-h-[300px] overflow-y-auto">
                  {selectedGroup.participants?.map((p) => {
                    const isAdmin = p.admin === 'admin' || p.admin === 'superadmin'
                    const isSuperAdmin = p.admin === 'superadmin'
                    const displayNumber = p.id.replace('@s.whatsapp.net', '')

                    return (
                      <div key={p.id} className="flex items-center justify-between rounded-md px-3 py-2 hover:bg-accent/50">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-mono">{displayNumber}</span>
                          {isAdmin && (
                            <Badge variant="outline" className="text-xs">
                              <Crown className="mr-1 h-3 w-3" />
                              {isSuperAdmin ? t('Dono') : t('Admin')}
                            </Badge>
                          )}
                        </div>

                        {!isSuperAdmin && (
                          <div className="flex gap-1">
                            {isAdmin ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDemote(p.id)}
                                disabled={demoteParticipants.isPending}
                                title={t('Rebaixar')}
                              >
                                <ShieldOff className="h-4 w-4" />
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handlePromote(p.id)}
                                disabled={promoteParticipants.isPending}
                                title={t('Promover a admin')}
                              >
                                <Shield className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveParticipant(p.id)}
                              disabled={removeParticipants.isPending}
                              title={t('Remover')}
                              className="text-destructive hover:text-destructive"
                            >
                              <UserMinus className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    )
                  })}

                  {(!selectedGroup.participants || selectedGroup.participants.length === 0) && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      {t('Nenhum participante carregado')}
                    </p>
                  )}
                </div>
              </TabsContent>

              {/* Settings tab */}
              <TabsContent value="settings" className="space-y-4">
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium">{t('Quem pode enviar mensagens')}</label>
                    <div className="flex gap-2 mt-1">
                      <Button
                        size="sm"
                        variant={selectedGroup.announce ? 'default' : 'outline'}
                        onClick={() => handleToggleSetting('announcement')}
                        disabled={updateGroupSetting.isPending}
                      >
                        <Shield className="mr-2 h-4 w-4" />
                        {t('Somente admins')}
                      </Button>
                      <Button
                        size="sm"
                        variant={!selectedGroup.announce ? 'default' : 'outline'}
                        onClick={() => handleToggleSetting('not_announcement')}
                        disabled={updateGroupSetting.isPending}
                      >
                        <Users className="mr-2 h-4 w-4" />
                        {t('Todos')}
                      </Button>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium">{t('Quem pode editar configuracoes')}</label>
                    <div className="flex gap-2 mt-1">
                      <Button
                        size="sm"
                        variant={selectedGroup.restrict ? 'default' : 'outline'}
                        onClick={() => handleToggleSetting('locked')}
                        disabled={updateGroupSetting.isPending}
                      >
                        <Shield className="mr-2 h-4 w-4" />
                        {t('Somente admins')}
                      </Button>
                      <Button
                        size="sm"
                        variant={!selectedGroup.restrict ? 'default' : 'outline'}
                        onClick={() => handleToggleSetting('unlocked')}
                        disabled={updateGroupSetting.isPending}
                      >
                        <Users className="mr-2 h-4 w-4" />
                        {t('Todos')}
                      </Button>
                    </div>
                  </div>

                  <Separator />

                  {/* Ephemeral Messages */}
                  <div>
                    <label className="text-sm font-medium">{t('Mensagens temporarias')}</label>
                    <p className="text-xs text-muted-foreground mb-2">
                      {t('Mensagens desaparecem apos o periodo selecionado')}
                    </p>
                    <div className="flex gap-2 flex-wrap mt-1">
                      {[
                        { label: t('Desativado'), value: 0 },
                        { label: '24h', value: 86400 },
                        { label: '7 dias', value: 604800 },
                        { label: '90 dias', value: 7776000 },
                      ].map((opt) => (
                        <Button
                          key={opt.value}
                          size="sm"
                          variant={selectedGroup.ephemeralExpiration === opt.value ? 'default' : 'outline'}
                          disabled={toggleEphemeral.isPending}
                          onClick={() => {
                            if (!selectedGroup) return
                            toggleEphemeral.mutate(
                              { groupJid: selectedGroup.id, expiration: opt.value },
                              {
                                onSuccess: () => toast.success(t('Configuracao atualizada!')),
                                onError: (e) => toast.error(e.message),
                              }
                            )
                          }}
                        >
                          {opt.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <Separator />

                  {/* Send Group Invite */}
                  <div>
                    <label className="text-sm font-medium">{t('Enviar convite do grupo')}</label>
                    <p className="text-xs text-muted-foreground mb-2">
                      {t('Envie o link de convite por mensagem para numeros (com DDI, ex: 5511999999999)')}
                    </p>
                    <div className="flex gap-2 mt-1">
                      <Input
                        placeholder={t('Numeros separados por virgula')}
                        value={inviteNumbers}
                        onChange={(e) => setInviteNumbers(e.target.value)}
                        className="flex-1"
                      />
                      <Button
                        size="sm"
                        disabled={sendGroupInvite.isPending || !inviteNumbers.trim()}
                        onClick={() => {
                          if (!selectedGroup) return
                          const nums = inviteNumbers.split(',').map(n => n.trim()).filter(Boolean)
                          if (nums.length === 0) return
                          sendGroupInvite.mutate(
                            { groupJid: selectedGroup.id, numbers: nums },
                            {
                              onSuccess: () => {
                                toast.success(t('Convites enviados!'))
                                setInviteNumbers('')
                              },
                              onError: (e) => toast.error(e.message),
                            }
                          )
                        }}
                      >
                        <Send className="mr-2 h-4 w-4" />
                        {t('Enviar')}
                      </Button>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ─── Channels Tab ────────────────────────────────────────

function ChannelsTab() {
  const { t } = useLanguage()
  const { data: instance } = useWhatsappStatus()
  const isConnected = instance?.status === 'open'
  const { data: channels, isLoading } = useWhatsappChannels(isConnected)
  const sendToChannel = useSendToChannel()

  const [selectedChannel, setSelectedChannel] = useState<string | null>(null)
  const [messageText, setMessageText] = useState('')

  if (!isConnected) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-12">
          <Power className="h-12 w-12 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{t('Conecte sua sessao WhatsApp para ver canais.')}</p>
        </CardContent>
      </Card>
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2].map(i => <Skeleton key={i} className="h-16 w-full" />)}
      </div>
    )
  }

  const channelList = Array.isArray(channels) ? channels : []

  const handleSend = () => {
    if (!selectedChannel || !messageText.trim()) {
      toast.error(t('Selecione um canal e digite uma mensagem'))
      return
    }
    sendToChannel.mutate(
      { channelJid: selectedChannel, text: messageText },
      {
        onSuccess: () => { toast.success(t('Mensagem enviada!')); setMessageText('') },
        onError: (e) => toast.error(e.message),
      }
    )
  }

  return (
    <div className="space-y-4">
      {channelList.length > 0 ? (
        <>
          <div className="grid gap-3">
            {channelList.map((ch) => (
              <Card
                key={ch.id}
                className={`cursor-pointer transition-colors ${selectedChannel === ch.id ? 'ring-2 ring-primary' : 'hover:bg-accent/50'}`}
                onClick={() => setSelectedChannel(ch.id)}
              >
                <CardContent className="flex items-center gap-3 py-3 px-4">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
                    <Hash className="h-4 w-4 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{ch.name || ch.pushName || ch.id}</div>
                    <div className="text-xs text-muted-foreground font-mono">{ch.id}</div>
                  </div>
                  {selectedChannel === ch.id && <Check className="h-4 w-4 text-primary" />}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Send message to channel */}
          {selectedChannel && (
            <Card>
              <CardContent className="space-y-3 py-4">
                <label className="text-sm font-medium">{t('Enviar mensagem ao canal')}</label>
                <Textarea
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  placeholder={t('Digite sua mensagem...')}
                  rows={3}
                />
                <Button onClick={handleSend} disabled={sendToChannel.isPending}>
                  <Send className="mr-2 h-4 w-4" />
                  {sendToChannel.isPending ? t('Enviando...') : t('Enviar')}
                </Button>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-8">
            <Hash className="h-10 w-10 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">{t('Nenhum canal encontrado')}</p>
            <p className="text-xs text-muted-foreground text-center max-w-md">
              {t('Canais que voce participa no WhatsApp aparecerao aqui.')}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// ─── Broadcast Tab ───────────────────────────────────────

interface BroadcastProgress {
  current: number
  total: number
  sent: number
  failed: number
  lastGroup: string
  lastStatus: string
  step?: number
}

// ── WhatsApp formatting helpers ──

function wrapSelection(
  textareaRef: React.RefObject<HTMLTextAreaElement | null>,
  value: string,
  setValue: (v: string) => void,
  prefix: string,
  suffix: string,
) {
  const ta = textareaRef.current
  if (!ta) return
  const start = ta.selectionStart
  const end = ta.selectionEnd
  const selected = value.substring(start, end)
  const before = value.substring(0, start)
  const after = value.substring(end)
  const newValue = before + prefix + (selected || 'texto') + suffix + after
  setValue(newValue)
  // Reposition cursor after React re-render
  requestAnimationFrame(() => {
    ta.focus()
    const cursorPos = selected ? start + prefix.length + selected.length + suffix.length : start + prefix.length + 5 + suffix.length
    ta.setSelectionRange(start + prefix.length, selected ? start + prefix.length + selected.length : start + prefix.length + 5)
  })
}

function renderWhatsappFormatted(text: string) {
  // Escape HTML first
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
  // WhatsApp formatting: ```code```, *bold*, _italic_, ~strike~
  html = html.replace(/```([\s\S]*?)```/g, '<code class="bg-muted px-1 rounded text-xs font-mono">$1</code>')
  html = html.replace(/\*([^*]+)\*/g, '<strong>$1</strong>')
  html = html.replace(/(?<![a-zA-Z0-9])_([^_]+)_(?![a-zA-Z0-9])/g, '<em>$1</em>')
  html = html.replace(/~([^~]+)~/g, '<s>$1</s>')
  return html
}

// ── Media Library Picker Dialog ──

function MediaPickerDialog({
  open,
  onClose,
  onSelect,
}: {
  open: boolean
  onClose: () => void
  onSelect: (url: string) => void
}) {
  const { data: folders } = useMediaFolders()
  const [selectedFolderId, setSelectedFolderId] = useState('')

  const allFolders = folders || []
  const selectedFolder = allFolders.find(f => f.id === selectedFolderId)
  const images = selectedFolder?.images.filter(img => img.mediaType === 'image') || []

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderOpen className="h-4 w-4" />
            Selecionar Imagem da Biblioteca
          </DialogTitle>
        </DialogHeader>

        <div className="flex gap-3 flex-1 min-h-0 overflow-hidden">
          {/* Folder tree */}
          <div className="w-[220px] shrink-0">
            <FolderTreePicker
              folders={allFolders}
              selectedId={selectedFolderId}
              onSelect={setSelectedFolderId}
              className="h-full"
            />
          </div>

          {/* Image grid */}
          <div className="flex-1 overflow-y-auto">
            {!selectedFolderId ? (
              <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
                Selecione uma pasta
              </div>
            ) : images.length === 0 ? (
              <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
                Nenhuma imagem nesta pasta
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {images.map(img => (
                  <button
                    key={img.id}
                    type="button"
                    className="relative aspect-square rounded-lg overflow-hidden border-2 border-transparent hover:border-primary transition-colors group"
                    onClick={() => {
                      onSelect(img.url)
                      onClose()
                    }}
                  >
                    <img
                      src={img.url}
                      alt={img.filename}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                      <Check className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function BroadcastTab() {
  const { t } = useLanguage()
  const qc = useQueryClient()
  const { data: instance } = useWhatsappStatus()
  const isConnected = instance?.status === 'open'
  const { data: groups, isLoading: groupsLoading } = useWhatsappGroups(isConnected)

  // Build instance JID for admin filtering
  const instanceJid = instance?.phoneNumber
    ? `${instance.phoneNumber.replace(/\D/g, '')}@s.whatsapp.net`
    : null

  // Filter to admin groups only (same logic as GroupsTab)
  const adminGroups = useMemo(() => {
    if (!groups) return []
    return groups.filter(g => {
      if (!g.participants || !instanceJid) return false
      return g.participants.some(
        p => (p.phoneNumber === instanceJid || p.id === instanceJid) && (p.admin === 'admin' || p.admin === 'superadmin')
      )
    })
  }, [groups, instanceJid])

  const [selectedJids, setSelectedJids] = useState<Set<string>>(new Set())
  const [message, setMessage] = useState('')
  const [imageUrl, setImageUrl] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [progress, setProgress] = useState<BroadcastProgress | null>(null)
  const [result, setResult] = useState<{ sent: number; failed: number; total: number } | null>(null)
  const [historyPage, setHistoryPage] = useState(1)
  const [showPreview, setShowPreview] = useState(false)

  // New feature states
  const [showMediaPicker, setShowMediaPicker] = useState(false)
  const [useManualUrl, setUseManualUrl] = useState(false)
  const [scheduleEnabled, setScheduleEnabled] = useState(false)
  const [scheduledAt, setScheduledAt] = useState('')
  const [flowEnabled, setFlowEnabled] = useState(false)
  const [flowSteps, setFlowSteps] = useState<WhatsappBroadcastFlowStep[]>([
    { message: '', imageUrl: '', delay: 1 },
    { message: '', imageUrl: '', delay: 1 },
  ])
  const [flowMediaPickerIdx, setFlowMediaPickerIdx] = useState<number | null>(null)

  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const formTopRef = useRef<HTMLDivElement>(null)
  const flowTextareaRefs = useRef<(HTMLTextAreaElement | null)[]>([])

  const { data: historyData } = useWhatsappBroadcastHistory(historyPage)
  const deleteBroadcast = useDeleteWhatsappBroadcast()
  const cancelBroadcast = useCancelWhatsappBroadcast()

  const mountedRef = useRef(true)
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  const toggleGroup = (jid: string) => {
    setSelectedJids(prev => {
      const next = new Set(prev)
      if (next.has(jid)) next.delete(jid)
      else next.add(jid)
      return next
    })
  }

  const toggleAll = () => {
    if (selectedJids.size === adminGroups.length) {
      setSelectedJids(new Set())
    } else {
      setSelectedJids(new Set(adminGroups.map(g => g.id)))
    }
  }

  const processPreview = (msg: string) => {
    const now = new Date()
    const hour = now.getHours()
    let saudacao = 'Boa noite'
    if (hour >= 5 && hour < 12) saudacao = 'Bom dia'
    else if (hour >= 12 && hour < 18) saudacao = 'Boa tarde'

    const pad = (n: number) => n.toString().padStart(2, '0')
    const data = `${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()}`
    const hora = `${pad(now.getHours())}:${pad(now.getMinutes())}`

    return msg
      .replace(/\{\{data\}\}/g, data)
      .replace(/\{\{hora\}\}/g, hora)
      .replace(/\{\{saudacao\}\}/g, saudacao)
  }

  // ── Flow step helpers ──
  const updateFlowStep = (idx: number, field: keyof WhatsappBroadcastFlowStep, value: string | number) => {
    setFlowSteps(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s))
  }
  const addFlowStep = () => {
    setFlowSteps(prev => [...prev, { message: '', imageUrl: '', delay: 1 }])
  }
  const removeFlowStep = (idx: number) => {
    if (flowSteps.length <= 2) return
    setFlowSteps(prev => prev.filter((_, i) => i !== idx))
  }

  // ── Resend from history ──
  const handleResend = (h: typeof history[number]) => {
    // Populate form with history data
    if (h.flow_steps && h.flow_steps.length >= 2) {
      setFlowEnabled(true)
      setFlowSteps(h.flow_steps.map(s => ({ message: s.message, imageUrl: s.imageUrl || '', delay: s.delay })))
      setMessage(h.message || '')
      setImageUrl('')
    } else {
      setFlowEnabled(false)
      setMessage(h.message)
      setImageUrl(h.image_url || '')
    }

    // Select the same groups
    const historyJids = h.groups.map(g => g.jid)
    const validJids = adminGroups.filter(g => historyJids.includes(g.id)).map(g => g.id)
    setSelectedJids(new Set(validJids))

    setScheduleEnabled(false)
    setScheduledAt('')

    // Scroll to top
    formTopRef.current?.scrollIntoView({ behavior: 'smooth' })
    toast.info(t('Formulario preenchido — edite e envie.'))
  }

  const handleSend = useCallback(async () => {
    if (selectedJids.size === 0) {
      toast.error(t('Selecione pelo menos um grupo'))
      return
    }

    if (flowEnabled) {
      const emptyStep = flowSteps.find(s => !s.message.trim())
      if (emptyStep) {
        toast.error(t('Todas as etapas precisam de mensagem'))
        return
      }
    } else if (!message.trim()) {
      toast.error(t('Digite uma mensagem'))
      return
    }

    setIsSending(true)
    setProgress(null)
    setResult(null)

    const controller = new AbortController()
    abortRef.current = controller

    const body: Record<string, unknown> = {
      message: flowEnabled ? flowSteps[0]?.message.trim() || '' : message.trim(),
      groups: Array.from(selectedJids),
    }

    if (flowEnabled) {
      body.flow_steps = flowSteps.map(s => ({
        message: s.message.trim(),
        imageUrl: s.imageUrl || null,
        delay: s.delay,
      }))
    } else {
      if (imageUrl) body.imageUrl = imageUrl
    }

    if (scheduleEnabled && scheduledAt) {
      body.scheduled_at = new Date(scheduledAt).toISOString()
    }

    try {
      const resp = await fetch(`${BASE}/whatsapp/broadcast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(body),
        signal: controller.signal,
      })

      // Scheduled: JSON response (no SSE)
      if (scheduleEnabled && scheduledAt) {
        const data = await resp.json()
        if (!resp.ok) {
          toast.error(data.error || t('Erro ao agendar disparo'))
        } else {
          toast.success(t('Disparo agendado com sucesso!'))
          qc.invalidateQueries({ queryKey: ['whatsapp', 'broadcast-history'] })
        }
        setIsSending(false)
        return
      }

      if (!resp.ok || !resp.body) {
        const err = await resp.json().catch(() => ({ error: 'Erro ao iniciar disparo' }))
        toast.error(err.error || t('Erro ao iniciar disparo'))
        setIsSending(false)
        return
      }

      const reader = resp.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let finalResult: { sent: number; failed: number; total: number } | null = null

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        if (!mountedRef.current) { reader.cancel(); break }
        buffer += decoder.decode(value, { stream: true })

        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        let eventType = ''
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7)
          } else if (line.startsWith('data: ') && eventType) {
            try {
              const data = JSON.parse(line.slice(6))
              if (eventType === 'start') {
                if (mountedRef.current) setProgress({ current: 0, total: data.total, sent: 0, failed: 0, lastGroup: '', lastStatus: '' })
              } else if (eventType === 'step_start') {
                if (mountedRef.current) toast.info(`${t('Etapa')} ${data.step}/${data.totalSteps}`)
              } else if (eventType === 'progress') {
                if (mountedRef.current) setProgress(data)
              } else if (eventType === 'done') {
                finalResult = { sent: data.sent, failed: data.failed, total: data.total }
              }
            } catch { /* ignore */ }
            eventType = ''
          }
        }
      }

      if (mountedRef.current && finalResult) {
        setResult(finalResult)
        toast.success(`${t('Disparo concluido!')} ${finalResult.sent} ${t('enviados')}`)
        qc.invalidateQueries({ queryKey: ['whatsapp', 'broadcast-history'] })
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') return
      if (mountedRef.current) toast.error(t('Erro ao enviar disparo'))
    } finally {
      abortRef.current = null
      if (mountedRef.current) setIsSending(false)
    }
  }, [selectedJids, message, imageUrl, qc, t, flowEnabled, flowSteps, scheduleEnabled, scheduledAt])

  const handleCancel = () => {
    if (abortRef.current) {
      abortRef.current.abort()
      abortRef.current = null
      setIsSending(false)
      toast.info(t('Disparo cancelado'))
    }
  }

  if (!isConnected) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-12">
          <Power className="h-12 w-12 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{t('Conecte sua sessao WhatsApp para disparar mensagens.')}</p>
        </CardContent>
      </Card>
    )
  }

  if (groupsLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
      </div>
    )
  }

  const progressPercent = progress ? Math.round((progress.current / progress.total) * 100) : 0
  const history = historyData?.data || []
  const historyLastPage = historyData?.last_page || 1

  // Check if form has valid content for sending
  const canSend = selectedJids.size > 0 && (flowEnabled ? flowSteps.every(s => s.message.trim()) : message.trim())

  // ── Formatting toolbar component ──
  const FormattingToolbar = ({ taRef, value, setValue }: { taRef: React.RefObject<HTMLTextAreaElement | null>; value: string; setValue: (v: string) => void }) => (
    <div className="flex items-center gap-1 border rounded-md p-1 bg-muted/30">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0 font-bold"
        onClick={() => wrapSelection(taRef, value, setValue, '*', '*')}
        title={t('Negrito')}
      >
        <Bold className="h-3.5 w-3.5" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0 italic"
        onClick={() => wrapSelection(taRef, value, setValue, '_', '_')}
        title={t('Italico')}
      >
        <Italic className="h-3.5 w-3.5" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0 line-through"
        onClick={() => wrapSelection(taRef, value, setValue, '~', '~')}
        title={t('Tachado')}
      >
        <Strikethrough className="h-3.5 w-3.5" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0 font-mono"
        onClick={() => wrapSelection(taRef, value, setValue, '```', '```')}
        title={t('Monoespaco')}
      >
        <Code className="h-3.5 w-3.5" />
      </Button>
    </div>
  )

  return (
    <div className="space-y-6" ref={formTopRef}>
      {/* Media picker dialog */}
      <MediaPickerDialog
        open={showMediaPicker}
        onClose={() => setShowMediaPicker(false)}
        onSelect={(url) => {
          if (flowMediaPickerIdx !== null) {
            updateFlowStep(flowMediaPickerIdx, 'imageUrl', url)
            setFlowMediaPickerIdx(null)
          } else {
            setImageUrl(url)
          }
        }}
      />

      {/* Group selector */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              {t('Selecionar Grupos')}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={toggleAll}
              disabled={isSending}
            >
              {selectedJids.size === adminGroups.length && adminGroups.length > 0 ? t('Desmarcar todos') : t('Selecionar todos')}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {adminGroups.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              {t('Nenhum grupo da plataforma encontrado. Crie grupos na aba Grupos.')}
            </p>
          ) : (
            <div className="grid gap-2 max-h-[250px] overflow-y-auto">
              {adminGroups.map((group) => (
                <label
                  key={group.id}
                  className="flex items-center gap-3 rounded-md border px-3 py-2 cursor-pointer hover:bg-accent/50 transition-colors"
                >
                  <Checkbox
                    checked={selectedJids.has(group.id)}
                    onCheckedChange={() => toggleGroup(group.id)}
                    disabled={isSending}
                  />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium truncate block">{group.subject}</span>
                  </div>
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    {group.size || group.participants?.length || 0}
                  </span>
                </label>
              ))}
            </div>
          )}
          {selectedJids.size > 0 && (
            <p className="text-xs text-muted-foreground mt-2">
              {selectedJids.size} {t('grupo(s) selecionado(s)')}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Mode toggles */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-6 py-4">
          <div className="flex items-center gap-2">
            <Switch
              id="schedule-toggle"
              checked={scheduleEnabled}
              onCheckedChange={(v) => { setScheduleEnabled(v); if (!v) setScheduledAt('') }}
              disabled={isSending}
            />
            <Label htmlFor="schedule-toggle" className="text-sm flex items-center gap-1.5 cursor-pointer">
              <CalendarClock className="h-4 w-4" />
              {t('Agendar')}
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="flow-toggle"
              checked={flowEnabled}
              onCheckedChange={setFlowEnabled}
              disabled={isSending}
            />
            <Label htmlFor="flow-toggle" className="text-sm flex items-center gap-1.5 cursor-pointer">
              <GitBranch className="h-4 w-4" />
              {t('Fluxo de mensagens')}
            </Label>
          </div>
        </CardContent>
      </Card>

      {/* Schedule datetime picker */}
      {scheduleEnabled && (
        <Card>
          <CardContent className="py-4">
            <label className="text-sm font-medium flex items-center gap-2 mb-2">
              <CalendarClock className="h-4 w-4" />
              {t('Data e Hora do envio')}
            </label>
            <Input
              type="datetime-local"
              value={scheduledAt}
              onChange={e => setScheduledAt(e.target.value)}
              disabled={isSending}
              min={new Date().toISOString().slice(0, 16)}
            />
          </CardContent>
        </Card>
      )}

      {/* Message (simple mode) */}
      {!flowEnabled && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Send className="h-4 w-4" />
              {t('Mensagem')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Formatting toolbar */}
            <FormattingToolbar taRef={textareaRef} value={message} setValue={setMessage} />

            <Textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('Digite sua mensagem... Use {{saudacao}}, {{data}}, {{hora}} como variaveis')}
              rows={4}
              disabled={isSending}
            />
            <p className="text-xs text-muted-foreground">
              {t('Variaveis')}: <code className="bg-muted px-1 rounded">{'{{saudacao}}'}</code> <code className="bg-muted px-1 rounded">{'{{data}}'}</code> <code className="bg-muted px-1 rounded">{'{{hora}}'}</code>
            </p>

            {/* Image selector */}
            <div>
              <label className="text-sm font-medium flex items-center gap-2 mb-2">
                <Image className="h-4 w-4" />
                {t('Imagem')} ({t('opcional')})
              </label>

              <div className="flex items-center gap-2 mb-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => { setFlowMediaPickerIdx(null); setShowMediaPicker(true) }}
                  disabled={isSending}
                >
                  <FolderOpen className="mr-2 h-4 w-4" />
                  {t('Selecionar da Biblioteca')}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setUseManualUrl(!useManualUrl)}
                  disabled={isSending}
                >
                  <Link2 className="mr-1 h-3 w-3" />
                  {useManualUrl ? t('Ocultar URL') : t('URL manual')}
                </Button>
                {imageUrl && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-destructive"
                    onClick={() => setImageUrl('')}
                  >
                    <X className="mr-1 h-3 w-3" />
                    {t('Remover')}
                  </Button>
                )}
              </div>

              {useManualUrl && (
                <Input
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="https://exemplo.com/imagem.jpg"
                  disabled={isSending}
                />
              )}

              {imageUrl && (
                <div className="mt-2">
                  <img
                    src={imageUrl}
                    alt="Preview"
                    className="max-h-32 rounded-lg object-contain border"
                    onError={(e) => (e.currentTarget.style.display = 'none')}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('A mensagem sera enviada como legenda da imagem.')}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Flow mode */}
      {flowEnabled && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <GitBranch className="h-4 w-4" />
              {t('Fluxo de Mensagens')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {flowSteps.map((step, idx) => (
              <div key={idx} className="rounded-lg border p-3 space-y-2 relative">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-muted-foreground">
                    {t('Etapa')} {idx + 1}
                  </span>
                  {flowSteps.length > 2 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                      onClick={() => removeFlowStep(idx)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>

                {/* Step formatting toolbar */}
                <FormattingToolbar
                  taRef={{ current: flowTextareaRefs.current[idx] } as React.RefObject<HTMLTextAreaElement>}
                  value={step.message}
                  setValue={(v) => updateFlowStep(idx, 'message', v)}
                />

                <Textarea
                  ref={(el) => { flowTextareaRefs.current[idx] = el }}
                  value={step.message}
                  onChange={(e) => updateFlowStep(idx, 'message', e.target.value)}
                  placeholder={`${t('Mensagem da etapa')} ${idx + 1}`}
                  rows={3}
                  disabled={isSending}
                />

                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => { setFlowMediaPickerIdx(idx); setShowMediaPicker(true) }}
                    disabled={isSending}
                  >
                    <ImageIcon className="mr-1 h-3 w-3" />
                    {step.imageUrl ? t('Trocar imagem') : t('Imagem')}
                  </Button>
                  {step.imageUrl && (
                    <>
                      <img src={step.imageUrl} alt="" className="h-8 w-8 rounded object-cover border" />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 text-destructive"
                        onClick={() => updateFlowStep(idx, 'imageUrl', '')}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </>
                  )}
                </div>

                {idx < flowSteps.length - 1 && (
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-muted-foreground">{t('Delay apos esta etapa')}:</label>
                    <Input
                      type="number"
                      min={0}
                      value={step.delay}
                      onChange={e => updateFlowStep(idx, 'delay', parseInt(e.target.value) || 0)}
                      className="w-20 h-7 text-xs"
                      disabled={isSending}
                    />
                    <span className="text-xs text-muted-foreground">{t('min')}</span>
                  </div>
                )}
              </div>
            ))}

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addFlowStep}
              disabled={isSending}
            >
              <Plus className="mr-1 h-3 w-3" />
              {t('Adicionar etapa')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Preview */}
      {(flowEnabled ? flowSteps.some(s => s.message.trim()) : message.trim()) && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 cursor-pointer" onClick={() => setShowPreview(!showPreview)}>
              <Eye className="h-4 w-4" />
              {t('Preview')}
              <Badge variant="outline" className="text-xs ml-auto">{showPreview ? t('Ocultar') : t('Mostrar')}</Badge>
            </CardTitle>
          </CardHeader>
          {showPreview && (
            <CardContent className="space-y-3">
              {flowEnabled ? (
                flowSteps.map((step, idx) => (
                  <div key={idx} className="space-y-1">
                    <span className="text-xs font-semibold text-muted-foreground">{t('Etapa')} {idx + 1}</span>
                    {step.imageUrl && (
                      <img src={step.imageUrl} alt="" className="max-h-32 rounded-lg object-contain" onError={(e) => (e.currentTarget.style.display = 'none')} />
                    )}
                    <div
                      className="rounded-lg bg-muted/50 p-3 text-sm whitespace-pre-wrap"
                      dangerouslySetInnerHTML={{ __html: renderWhatsappFormatted(processPreview(step.message)) }}
                    />
                    {idx < flowSteps.length - 1 && (
                      <p className="text-xs text-muted-foreground italic">
                        {t('Delay')}: {step.delay} {t('min')}
                      </p>
                    )}
                  </div>
                ))
              ) : (
                <>
                  {imageUrl && (
                    <img src={imageUrl} alt="Preview" className="max-h-40 rounded-lg object-contain" onError={(e) => (e.currentTarget.style.display = 'none')} />
                  )}
                  <div
                    className="rounded-lg bg-muted/50 p-4 text-sm whitespace-pre-wrap"
                    dangerouslySetInnerHTML={{ __html: renderWhatsappFormatted(processPreview(message)) }}
                  />
                </>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {/* Send / Schedule button */}
      <div className="flex gap-3">
        {!isSending ? (
          <Button
            onClick={handleSend}
            disabled={!canSend || (scheduleEnabled && !scheduledAt)}
            className="flex-1"
          >
            {scheduleEnabled ? (
              <>
                <CalendarClock className="mr-2 h-4 w-4" />
                {t('Agendar para')} {selectedJids.size} {t('grupo(s)')}
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                {t('Enviar para')} {selectedJids.size} {t('grupo(s)')}
                {flowEnabled && ` (${flowSteps.length} ${t('etapas')})`}
              </>
            )}
          </Button>
        ) : (
          <Button variant="destructive" onClick={handleCancel} className="flex-1">
            <X className="mr-2 h-4 w-4" />
            {t('Cancelar')}
          </Button>
        )}
      </div>

      {/* Progress */}
      {isSending && progress && (
        <Card>
          <CardContent className="space-y-3 py-4">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('Enviando...')}
                {progress.step && <Badge variant="secondary" className="text-xs">{t('Etapa')} {progress.step}</Badge>}
              </span>
              <span className="font-mono">{progress.current}/{progress.total}</span>
            </div>

            {/* Progress bar */}
            <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
              <div
                className="h-full rounded-full bg-primary transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>

            <div className="flex gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3 text-green-500" />
                {progress.sent} {t('enviados')}
              </span>
              <span className="flex items-center gap-1">
                <XCircle className="h-3 w-3 text-red-500" />
                {progress.failed} {t('falhos')}
              </span>
            </div>

            {progress.lastGroup && (
              <p className="text-xs text-muted-foreground">
                {t('Ultimo')}: {progress.lastGroup} — {progress.lastStatus === 'ok' ? t('Enviado') : t('Falhou')}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Result */}
      {result && !isSending && (
        <Card className="border-green-500/30">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-6 w-6 text-green-500 shrink-0" />
              <div>
                <p className="font-medium text-sm">{t('Disparo concluido!')}</p>
                <p className="text-xs text-muted-foreground">
                  {result.sent} {t('enviados')} / {result.failed} {t('falhos')} / {result.total} {t('total')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* History */}
      <Separator />
      <div>
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <Clock className="h-4 w-4" />
          {t('Historico de Disparos')}
        </h3>

        {history.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">{t('Nenhum disparo realizado ainda.')}</p>
        ) : (
          <div className="space-y-2">
            {history.map((h) => (
              <Card key={h.id}>
                <CardContent className="flex items-center gap-3 py-3 px-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm truncate flex-1">{h.flow_steps ? `[${t('Fluxo')} ${h.flow_steps.length} ${t('etapas')}] ` : ''}{h.message}</p>
                      {h.status === 'pending' && (
                        <Badge variant="secondary" className="text-xs shrink-0">
                          <CalendarClock className="mr-1 h-3 w-3" />
                          {t('Agendado')}
                        </Badge>
                      )}
                      {h.status === 'sending' && (
                        <Badge className="text-xs shrink-0 bg-blue-500">
                          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                          {t('Enviando')}
                        </Badge>
                      )}
                      {h.status === 'failed' && (
                        <Badge variant="destructive" className="text-xs shrink-0">
                          {t('Falhou')}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1 flex-wrap">
                      <span>{new Date(h.created_at).toLocaleString('pt-BR')}</span>
                      {h.scheduled_at && h.status === 'pending' && (
                        <span className="text-amber-600">
                          {t('Envio')}: {new Date(h.scheduled_at).toLocaleString('pt-BR')}
                        </span>
                      )}
                      <span>{h.total_groups} {t('grupo(s)')}</span>
                      {h.status === 'completed' && (
                        <>
                          <span className="text-green-600">{h.sent_count} {t('ok')}</span>
                          {h.failed_count > 0 && <span className="text-red-500">{h.failed_count} {t('falhos')}</span>}
                        </>
                      )}
                      {h.image_url && <Badge variant="outline" className="text-xs"><Image className="mr-1 h-3 w-3" />{t('Imagem')}</Badge>}
                      {h.flow_steps && <Badge variant="outline" className="text-xs"><GitBranch className="mr-1 h-3 w-3" />{t('Fluxo')}</Badge>}
                    </div>
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    {/* Resend button */}
                    {(h.status === 'completed' || h.status === 'failed') && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-primary"
                        onClick={() => handleResend(h)}
                        title={t('Reenviar')}
                      >
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                    )}

                    {/* Cancel scheduled */}
                    {h.status === 'pending' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() => {
                          cancelBroadcast.mutate(h.id, {
                            onSuccess: () => toast.success(t('Agendamento cancelado')),
                            onError: (e) => toast.error(e.message),
                          })
                        }}
                        title={t('Cancelar agendamento')}
                      >
                        <XCircle className="h-4 w-4" />
                      </Button>
                    )}

                    {/* Delete */}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={() => {
                        deleteBroadcast.mutate(h.id, {
                          onSuccess: () => toast.success(t('Removido')),
                          onError: (e) => toast.error(e.message),
                        })
                      }}
                      disabled={deleteBroadcast.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Pagination */}
            {historyLastPage > 1 && (
              <div className="flex items-center justify-center gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                  disabled={historyPage <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-xs text-muted-foreground">{historyPage} / {historyLastPage}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setHistoryPage(p => Math.min(historyLastPage, p + 1))}
                  disabled={historyPage >= historyLastPage}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Main Page ───────────────────────────────────────────

export function WhatsApp() {
  const { t } = useLanguage()

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <div>
        <h1 className="text-xl font-bold tracking-tight">{t('WhatsApp')}</h1>
        <p className="text-sm text-muted-foreground">{t('Gerencie sua sessao, grupos e canais do WhatsApp.')}</p>
      </div>

      <Tabs defaultValue="session">
        <TabsList>
          <TabsTrigger value="session">
            <Smartphone className="mr-2 h-4 w-4" />
            {t('Sessao')}
          </TabsTrigger>
          <TabsTrigger value="groups">
            <Users className="mr-2 h-4 w-4" />
            {t('Grupos')}
          </TabsTrigger>
          <TabsTrigger value="channels">
            <Hash className="mr-2 h-4 w-4" />
            {t('Canais')}
          </TabsTrigger>
          <TabsTrigger value="broadcast">
            <Send className="mr-2 h-4 w-4" />
            {t('Disparos')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="session">
          <SessionTab />
        </TabsContent>
        <TabsContent value="groups">
          <GroupsTab />
        </TabsContent>
        <TabsContent value="channels">
          <ChannelsTab />
        </TabsContent>
        <TabsContent value="broadcast">
          <BroadcastTab />
        </TabsContent>
      </Tabs>
    </div>
  )
}
